import { use, useEffect, useEffectEvent, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { useBookmarks, useMutateBookmarks } from "lib/bookmarks/use-bookmarks";
import { getQuoteElement, getQuoteIdAt } from "lib/highlights/use-highlights";
import { useTranslation } from "lib/i18n/use-translation";
import { captureException } from "lib/logger";
import { ModalsContext } from "lib/modals/use-modals";
import { NavigationContext } from "lib/navigation/use-navigation";
import { usePaths } from "lib/navigation/use-paths";
import { NotificationsContext } from "lib/notifications/use-notifications";
import { type QuoteColor } from "lib/quotes/get-quotes";
import { useQuotes } from "lib/quotes/use-quotes";
import {
  expandRangeToWordBoundaries,
  expandSelectionToWordBoundaries,
} from "lib/selection/expand-words";
import { SelectionColorContext } from "lib/selection/selection-color";
import { stripSoftHyphens } from "lib/text/text-content";
import { parseXpointer, rangeToXpointers } from "lib/text/xpointer-range";
import { type FloatingMenuAnchor } from "lib/ui/floating-menu";
import { isTouch } from "lib/utils/device";

export type SelectionMenuMode =
  | "createQuote"
  | "updateQuote"
  | "bookmarkParagraph"
  | "bookmarkPage";

interface Position {
  x: number;
  y: number;
}

// Xpointers — стабильные координаты, не зависят от мутаций DOM.
// На iOS Safari тап по кнопке убирает нативное выделение и инвалидирует Range,
// но xpointers остаются валидными.
interface SelectionData {
  text: string;
  xpointers: { start: string; end: string };
}

interface QuoteData {
  id: string;
  color: QuoteColor;
  noteText: string;
  quoteText: string;
}

export function useSelectionMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<FloatingMenuAnchor>({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<SelectionMenuMode>("createQuote");
  const [selectionData, setSelectionData] = useState<SelectionData | null>(
    null
  );
  const [quoteData, setQuoteData] = useState<QuoteData | null>(null);
  const colorConfirmedRef = useRef(false);
  const [selectedColor, setSelectedColor] = useState<QuoteColor>("basic");
  const setSelectionColor = use(SelectionColorContext);
  // Xpointer закладки для режимов "bookmarkParagraph" / "bookmarkPage"
  const [bookmarkTarget, setBookmarkTarget] = useState<string | null>(null);

  const { t } = useTranslation();
  const { bookmarks } = useBookmarks();
  const { createBookmark, deleteBookmark } = useMutateBookmarks();
  const { createQuote, deleteQuote, updateQuote, quotes } = useQuotes();
  const { setBookmarksPanel, openShareQuote } = use(ModalsContext)!;
  const { showToast, showBookmarkNotification } = use(NotificationsContext)!;
  const { page } = use(NavigationContext)!;
  const { pageToXpointer } = usePaths();

  const currentPageXpointer = pageToXpointer(page);

  function closeMenu() {
    setIsOpen(false);
    setSelectionData(null);
    setQuoteData(null);
    setSelectedColor("basic");
    setSelectionColor(null);
    setBookmarkTarget(null);
  }

  function clearSelection() {
    window.getSelection()?.removeAllRanges();
  }

  // Захватывает текущее нативное выделение и конвертирует в xpointers.
  // Расширяет до границ слов без мутации Selection API (безопасно для iOS).
  function captureCurrentSelection(): {
    data: SelectionData;
    rect: DOMRect;
  } | null {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return null;

    const range = sel.getRangeAt(0);
    if (range.getClientRects().length === 0) return null;

    const contentEl = document.getElementById("content");
    const ancestor = range.commonAncestorContainer;

    let isInsideContent = false;
    if (ancestor.nodeType === Node.TEXT_NODE) {
      const parent = ancestor.parentElement;
      if (parent?.closest("#content") === contentEl) {
        isInsideContent = true;
      }
    } else if ((ancestor as Element).closest?.("#content") === contentEl) {
      isInsideContent = true;
    }

    if (!isInsideContent) return null;

    // Расширяем до слов на уровне Range (не трогая Selection API)
    const expanded = expandRangeToWordBoundaries(range);
    const effectiveRange = expanded?.expandedRange ?? range;
    const text = expanded ? expanded.text : stripSoftHyphens(sel.toString());

    const xpointers = rangeToXpointers(effectiveRange);
    if (!xpointers) return null;

    return {
      data: { text, xpointers },
      rect: effectiveRange.getBoundingClientRect(),
    };
  }

  function openMenuForSelection(): boolean {
    const captured = captureCurrentSelection();
    if (!captured) return false;

    setSelectionData(captured.data);
    setQuoteData(null);
    setMode("createQuote");
    setAnchor(captured.rect);
    setSelectedColor("basic");
    setSelectionColor("basic");
    setIsOpen(true);

    return true;
  }

  function openMenuForQuote(quoteId: string, point: Position) {
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote) return;

    setQuoteData({
      id: quoteId,
      color: (quote.color as QuoteColor) || "basic",
      noteText: quote.note_text || "",
      quoteText: quote.quote_text || "",
    });
    setSelectionData(null);
    setMode("updateQuote");
    setSelectedColor((quote.color as QuoteColor) || "basic");
    setAnchor(point);
    setIsOpen(true);
  }

  function resolveBookmarkTarget(point: Position): string | null {
    const clickedP = document.elementFromPoint(point.x, point.y)?.closest("p");
    const clickedPId = clickedP?.id;
    const isClickedPBookmarked = clickedPId
      ? bookmarks.some(b => b.xpointer === clickedPId)
      : false;

    return isClickedPBookmarked ? clickedPId! : currentPageXpointer;
  }

  const handleQuoteTap = useEffectEvent((e: MouseEvent) => {
    // На десктопе цитаты открываются правым кликом (contextmenu),
    // левый клик не должен ничего делать
    if (!isTouch()) return;

    const target = e.target as HTMLElement;
    if (!target.closest("#content")) return;

    const quoteId = getQuoteIdAt({ el: target, x: e.clientX, y: e.clientY });
    if (!quoteId) return;

    e.preventDefault();
    e.stopPropagation();

    const quoteElement = getQuoteElement(quoteId);
    const rect = quoteElement
      ? quoteElement.getBoundingClientRect()
      : target.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;

    openMenuForQuote(quoteId, { x: centerX, y: e.clientY });
  });

  const handleContextMenu = useEffectEvent((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest("#content")) return;

    // Если есть выделение текста — показываем меню для создания цитаты.
    // Работает и на десктопе (правый клик), и на Android (contextmenu после long-press)
    if (openMenuForSelection()) {
      e.preventDefault();
      return;
    }

    // Остальные действия — только десктоп (правый клик)
    if (isTouch()) return;

    const quoteId = getQuoteIdAt({ el: target, x: e.clientX, y: e.clientY });

    if (quoteId) {
      const quote = quotes.find(q => q.id === quoteId);
      if (quote) {
        e.preventDefault();
        openMenuForQuote(quoteId, { x: e.clientX, y: e.clientY });
        return;
      }
    }

    e.preventDefault();

    const point = { x: e.clientX, y: e.clientY };
    const clickedP = target.closest("p");

    setSelectionData(null);
    setQuoteData(null);
    setMode(clickedP?.id ? "bookmarkParagraph" : "bookmarkPage");
    setAnchor(point);
    setBookmarkTarget(resolveBookmarkTarget(point));
    setIsOpen(true);
  });

  const handleMouseUp = useEffectEvent(() => {
    if (isTouch()) return;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) {
      expandSelectionToWordBoundaries();
    }
  });

  const handleTouchEnd = useEffectEvent((e: TouchEvent) => {
    const target = e.target as HTMLElement;
    const inContent = !!target.closest("#content");
    const sel = window.getSelection();
    const collapsed = !sel || sel.isCollapsed;

    if (!inContent) return;

    if (collapsed) {
      if (isOpen) closeMenu();
      return;
    }

    if (!isOpen) {
      openMenuForSelection();
      return;
    }

    // Обновляем позицию если xpointers изменились
    const captured = captureCurrentSelection();
    if (captured) {
      const prev = selectionData?.xpointers;
      const next = captured.data.xpointers;
      if (prev?.start !== next.start || prev?.end !== next.end) {
        setSelectionData(captured.data);
      }
      setAnchor(captured.rect);
    }
  });

  // Убираем мягкие переносы при нативном копировании (Ctrl+C / Cmd+C)
  const handleCopy = useEffectEvent((e: ClipboardEvent) => {
    const sel = document.getSelection();
    if (!sel || sel.isCollapsed) return;

    const cleaned = stripSoftHyphens(sel.toString());
    if (cleaned.length > 500) {
      e.preventDefault();
      showToast(t("Можно скопировать не больше 500 символов за один раз"));
      return;
    }
    e.clipboardData?.setData("text/plain", cleaned);
    e.preventDefault();
    showToast(t("Цитата скопирована"), { variant: "info" });
  });

  useEffect(() => {
    document.addEventListener("click", handleQuoteTap, true);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("copy", handleCopy);

    return () => {
      document.removeEventListener("click", handleQuoteTap, true);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("copy", handleCopy);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    function handlePointerDown(e: PointerEvent) {
      // Тач: закрытие через touchend
      if (e.pointerType === "touch") return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu();
        clearSelection();
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  async function createQuoteFromSelection(color: QuoteColor = "basic") {
    if (!selectionData) return null;

    const { text, xpointers } = selectionData;

    const [quote, error] = await createQuote({
      start_xpointer: xpointers.start,
      end_xpointer: xpointers.end,
      quote_text: text,
      color,
    });

    if (error) {
      showToast(t("Не удалось сохранить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
      return null;
    }

    return quote;
  }

  async function onCreateQuote(color: QuoteColor = "basic") {
    const quote = await createQuoteFromSelection(color);
    if (quote) {
      closeMenu();
      clearSelection();
    }
  }

  async function onUpdateQuoteColor(color: QuoteColor) {
    if (!quoteData) return;

    if (color === quoteData.color) {
      closeMenu();
      return;
    }

    const [, error] = await updateQuote(quoteData.id, { color });
    if (error) {
      showToast(t("Не удалось изменить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
    } else {
      colorConfirmedRef.current = true;
    }
    closeMenu();
  }

  async function onDeleteQuote() {
    if (!quoteData) return;
    const [, error] = await deleteQuote(quoteData.id);
    if (error) {
      showToast(t("Не удалось удалить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
    }
    closeMenu();
  }

  // Находит id абзаца для закладки: из xpointer выделения или из элемента цитаты
  function getBookmarkParagraphId(): string | null {
    if (selectionData) {
      const parsed = parseXpointer(selectionData.xpointers.start);
      if (!parsed) return null;
      const el = document.getElementById(parsed.elementId);
      return el?.closest("p")?.id ?? el?.id ?? null;
    }

    if (quoteData) {
      const el = getQuoteElement(quoteData.id);
      return el?.closest("p")?.id || null;
    }

    return null;
  }

  async function onBookmark() {
    const pId = getBookmarkParagraphId();
    if (!pId) return;

    const existingBookmark = bookmarks.find(b => b.xpointer === pId);

    if (existingBookmark) {
      const [, error] = await deleteBookmark(existingBookmark.id);
      if (error) {
        showToast(t("Не удалось удалить закладку"));
      } else {
        showBookmarkNotification("deleted");
      }
    } else {
      const [, error] = await createBookmark(pId);
      if (error) {
        showToast(t("Не удалось сохранить закладку"));
      } else {
        showBookmarkNotification("added");
      }
    }

    closeMenu();
    clearSelection();
  }

  async function onPageBookmark() {
    if (!bookmarkTarget) return;

    const existingBookmark = bookmarks.find(b => b.xpointer === bookmarkTarget);
    if (existingBookmark) {
      const [, error] = await deleteBookmark(existingBookmark.id);
      if (error) {
        showToast(t("Не удалось удалить закладку"));
      } else {
        showBookmarkNotification("deleted");
      }
    } else {
      const [, error] = await createBookmark(bookmarkTarget);
      if (error) {
        showToast(t("Не удалось сохранить закладку"));
      } else {
        showBookmarkNotification("added");
      }
    }

    closeMenu();
  }

  async function onCopy() {
    const text = selectionData?.text || quoteData?.quoteText;
    if (text) {
      if (text.length > 500) {
        showToast(t("Можно скопировать не больше 500 символов за один раз"));
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        showToast(t("Цитата скопирована"), { variant: "info" });
      } catch {
        // Фоллбэк для iOS Safari, где Clipboard API может быть недоступен
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        try {
          textarea.select();
          const ok = document.execCommand("copy");
          if (ok) {
            showToast(t("Цитата скопирована"), { variant: "info" });
          } else {
            showToast(t("Не удалось скопировать текст"));
          }
        } catch (fallbackError) {
          showToast(t("Не удалось скопировать текст"));
          captureException(fallbackError as Error);
        } finally {
          document.body.removeChild(textarea);
        }
      }
    }
    closeMenu();
  }

  async function onAddNote() {
    if (!selectionData) return;

    const { text, xpointers } = selectionData;
    const quoteId = uuid();

    setBookmarksPanel({
      isOpen: true,
      activeTab: "quotes",
      selectedQuoteId: quoteId,
    });

    closeMenu();
    clearSelection();

    const [, error] = await createQuote({
      id: quoteId,
      start_xpointer: xpointers.start,
      end_xpointer: xpointers.end,
      quote_text: text,
      color: selectedColor,
    });

    if (error) {
      showToast(t("Не удалось сохранить цитату"), {
        message: t("Пожалуйста, обновите страницу и попробуйте снова"),
      });
    }
  }

  function onEditNote() {
    if (!quoteData) return;
    setBookmarksPanel({
      isOpen: true,
      activeTab: "quotes",
      selectedQuoteId: quoteData.id,
    });
    closeMenu();
  }

  async function onShare() {
    if (quoteData) {
      if (quoteData.quoteText.length < 30) {
        showToast(t("Цитата слишком короткая"), {
          message: t("Минимальная длина цитаты для публикации — 30 символов"),
        });
        closeMenu();
        return;
      }
      openShareQuote(quoteData.id, quoteData.quoteText);
      closeMenu();
      return;
    }

    const quote = await createQuoteFromSelection();
    if (!quote) return;

    if (quote.quote_text.length < 30) {
      showToast(t("Цитата слишком короткая"), {
        message: t("Минимальная длина цитаты для публикации — 30 символов"),
      });
      closeMenu();
      clearSelection();
      return;
    }
    openShareQuote(quote.id, quote.quote_text);
    closeMenu();
    clearSelection();
  }

  function selectColor(color: QuoteColor) {
    setSelectedColor(color);
    setSelectionColor(color);
  }

  const bookmarkParagraphId = getBookmarkParagraphId();
  const isBookmarked = bookmarkParagraphId
    ? bookmarks.some(b => b.xpointer === bookmarkParagraphId)
    : false;

  const isPageBookmarked = bookmarkTarget
    ? bookmarks.some(b => b.xpointer === bookmarkTarget)
    : false;

  return {
    isOpen,
    mode,
    anchor,
    menuRef,

    selectedColor,
    isBookmarked,
    isPageBookmarked,

    previewQuote: quoteData
      ? { id: quoteData.id, originalColor: quoteData.color }
      : null,
    previewXpointers: selectionData?.xpointers ?? null,
    colorConfirmedRef,

    onCreateQuote,
    onUpdateQuoteColor,
    onDeleteQuote,
    onBookmark,
    onPageBookmark,
    onCopy,
    onAddNote,
    onEditNote,
    onShare,

    selectColor,
    closeMenu,
  };
}
